/**
 * Email List Controller — signal-store-backed row selection, navigation, infinite scroll.
 *
 * Delegates all state to the central emailStore (window._emailStore).
 * Local responsibility: DOM lifecycle callbacks (rowTargetConnected, sentinelTargetConnected),
 * IntersectionObserver management via EmailInfiniteScroll helper,
 * and direct DOM paint of selection/active state from store signals.
 */

import { Controller } from '@hotwired/stimulus'
import { Modal } from 'bootstrap'
import { I18n } from 'i18n-js'
import { get, patch } from '@rails/request.js'
import { effect } from '@preact/signals'
import locales from '../locales.json'
import { listContext } from '../modules/email/list-context.js'
import { EmailInfiniteScroll } from '../modules/email/email-infinite-scroll.js'

export default class extends Controller {
  static targets = ['row', 'selectAll', 'toolbar', 'bulkToolbar', 'scrollSentinel']

  connect() {
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content || 'hu'

    this._infiniteScroll = new EmailInfiniteScroll()
    this._disposers = []

    const store = this._store()
    if (!store) return

    // ── Signal effects: repaint rows when selected/active changes ──────────

    // selectedIds changed → repaint all rows + toggle toolbars + sync counter
    this._disposers.push(
      effect(() => {
        const selected = new Set(store.selectedIds.value)
        const count    = selected.size
        const hasSel   = count > 0

        this.rowTargets.forEach(row => {
          const isSelected = selected.has(parseInt(row.dataset.emailId))
          row.classList.toggle('email-list-row--selected', isSelected)
          const cb = row.querySelector('input[type=checkbox]')
          if (cb) cb.checked = isSelected
        })

        if (this.hasToolbarTarget)     this.toolbarTarget.classList.toggle('d-none', hasSel)
        if (this.hasBulkToolbarTarget) this.bulkToolbarTarget.classList.toggle('d-none', !hasSel)

        const counter = this.hasBulkToolbarTarget
          ? this.bulkToolbarTarget.querySelector("[data-email-bulk-target='counter']")
          : null
        if (counter) {
          counter.textContent = count > 0
            ? this.i18n.t('emails.selected_count', { count })
            : ''
        }

        if (this.hasSelectAllTarget) {
          const total = this.rowTargets.length
          this.selectAllTarget.checked       = count > 0 && count >= total
          this.selectAllTarget.indeterminate = count > 0 && count < total
        }

        // Sync to the bulk controller if present (legacy outlet support)
        const bulkEl = this.hasBulkToolbarTarget ? this.bulkToolbarTarget : null
        if (bulkEl) {
          const bulkController = this.application.getControllerForElementAndIdentifier(bulkEl, 'email-bulk')
          if (bulkController) bulkController.idsValue = store.selectedIds.value
        }
      })
    )

    // currentThreadId changed → repaint active highlight
    this._disposers.push(
      effect(() => {
        const currentId = store.currentThreadId.value
        this.rowTargets.forEach(r => {
          r.classList.toggle(
            'email-list-row--active',
            !!currentId && r.dataset.emailId == currentId
          )
        })
      })
    )

    // listLoading changed → show/hide reading pane spinner
    this._disposers.push(
      effect(() => {
        const wrapper = document.querySelector('.email-reading-pane-wrapper')
        if (!wrapper) return
        wrapper.classList.toggle('email-reading-pane--loading', store.listLoading.value)
      })
    )
  }

  disconnect() {
    this._disposers.forEach(d => d())
    this._disposers = []
    clearTimeout(this._loadingTimeout)
    this._infiniteScroll?.disconnectAll()
  }

  // ── Stimulus lifecycle: per-element callbacks ────────────────────────────

  scrollSentinelTargetConnected(el) {
    this._infiniteScroll.attach(el)
  }

  scrollSentinelTargetDisconnected(el) {
    this._infiniteScroll.detach(el)
  }

  rowTargetConnected(row) {
    const store = this._store()
    if (!store) return

    const id = parseInt(row.dataset.emailId)

    // Active highlight
    const currentId = store.currentThreadId.value
    row.classList.toggle('email-list-row--active', !!currentId && id == currentId)

    // Selection state
    const isSelected = store.selectedIds.value.includes(id)
    row.classList.toggle('email-list-row--selected', isSelected)
    const cb = row.querySelector('input[type=checkbox]')
    if (cb) cb.checked = isSelected
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  selectRow(e) {
    const store = this._store()
    if (!store) return
    const id = parseInt(e.currentTarget.value)
    if (e.currentTarget.checked) {
      store.selectedIds.value = [...store.selectedIds.value, id]
    } else {
      store.selectedIds.value = store.selectedIds.value.filter(i => i !== id)
    }
  }

  selectAll(e) {
    const store = this._store()
    if (!store) return
    if (e.currentTarget.checked) {
      store.selectAll(this.rowTargets.map(r => parseInt(r.dataset.emailId)).filter(Boolean))
    } else {
      store.clearSelection()
    }
  }

  openInModal(event) {
    event.preventDefault()
    window.getSelection()?.removeAllRanges()

    const row = event.currentTarget.closest('.email-list-row')
    const url = row?.dataset.emailModalUrl
    if (!url) return

    const frame    = document.getElementById('email_thread_modal')
    const modalEl  = document.getElementById('email_thread_modal_container')
    if (!frame || !modalEl) return

    frame.removeAttribute('complete')
    frame.src = url

    Modal.getOrCreateInstance(modalEl).show()
  }

  async toggleStar(e) {
    e.preventDefault()
    e.stopPropagation()
    const emailId = e.params.emailId
    if (!emailId) return

    await patch(`/emails/threads/${emailId}/update_state`, {
      body: JSON.stringify({ action_type: 'star', ...listContext() }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  navigateRow(e) {
    const store   = this._store()
    const row     = e.currentTarget.closest('.email-list-row')
    const emailId = row?.dataset.emailId || e.params.emailId
    if (!emailId) return

    // Show loading spinner via store signal
    if (store) store.selectThread(emailId)

    // Safety net: clear spinner after 8s if thread never loads
    clearTimeout(this._loadingTimeout)
    this._loadingTimeout = setTimeout(() => {
      if (store) store.threadLoaded()
    }, 8000)

    const ctx = listContext()
    const qs  = new URLSearchParams(ctx).toString()
    const url = qs ? `/emails/threads/${emailId}?${qs}` : `/emails/threads/${emailId}`
    get(url, {
      responseKind: 'turbo-stream',
      headers: { Accept: 'text/vnd.turbo-stream.html' }
    })

    row?.classList.remove('email-unread')
  }

  stopRowClick(e) {
    e.stopPropagation()
  }

  // Called by email-thread controller or keyboard handler
  setActive(emailId) {
    const store = this._store()
    if (store) store.setCurrentThread(emailId)
  }

  clearActive() {
    const store = this._store()
    if (store) store.clearCurrentThread()
  }

  focusNext() {
    const rows    = this.rowTargets
    const store   = this._store()
    const current = rows.findIndex(r => r.dataset.emailId == store?.currentThreadId.value)
    const next    = rows[current + 1]
    if (next) next.querySelector("[data-action*='navigateRow']")?.click()
  }

  focusPrev() {
    const rows    = this.rowTargets
    const store   = this._store()
    const current = rows.findIndex(r => r.dataset.emailId == store?.currentThreadId.value)
    const prev    = rows[current - 1]
    if (prev) prev.querySelector("[data-action*='navigateRow']")?.click()
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  _store() {
    return window._emailStore || null
  }
}
